import { isMobilePhone, PhoneLocale, PhoneLocaleAlias } from "validator";
import {
  apacLocaleOptions,
  emeaLocaleOptions,
  localeOptions,
  naLocaleOptions,
} from "./localizationData";
import { Locale } from "@/dto/locale";

const applyFormat = (phoneNumber: string, format: string): string => {
  let formattedPhone = format;
  let charIndex = 0;

  // replace '#' with phone number
  formattedPhone = formattedPhone.replace(/#/g, () => {
    if (charIndex < phoneNumber.length) {
      return phoneNumber.charAt(charIndex++);
    }
    return "#";
  });

  // return an empty string if the phone number is short
  if (formattedPhone.includes("#")) {
    return "";
  }
  return formattedPhone;
};

/**
 * localizedPhoneNumber is a function that formats a given phone number with
 * localized way of presenting it, including area and country codes
 * @param phoneNumber   {string}    - phone number to format
 * @param locale        {string}    - current localization code
 * @param international {boolean}   - if the phone number should be displayed
 *                                    in international format
 * @param showError     {boolean}   - if the phone number is illegal,
 *                                    should error message be displayed
 * @returns string containing localized phone number, optionally formatted
 * as international with country code.
 */
export const localizedPhoneNumber = (
  phoneNumber: string,
  locale: Locale = "en-US",
  international = false
) => {
  // get current locale definition
  const currentLocaleOption = localeOptions[locale] || localeOptions["en-US"]!;
  // characters to remove from the phone number and format to make it possible to compare them
  const charactersToRemove = /[().\s,\/\\:+;-]/g;
  const cleanedPhoneNumber = phoneNumber.replace(charactersToRemove, "");
  let validationNumber = cleanedPhoneNumber;

  if (
    currentLocaleOption.numberPrefix &&
    currentLocaleOption.minimumLength &&
    !validationNumber.startsWith(currentLocaleOption.numberPrefix) &&
    validationNumber.length ===
      currentLocaleOption.minimumLength -
        (currentLocaleOption.numberPrefix?.length || 0)
  ) {
    validationNumber = currentLocaleOption.numberPrefix + cleanedPhoneNumber;
  }

  let formatToUse = currentLocaleOption.format;

  if (
    currentLocaleOption.hasDifferentFormats &&
    currentLocaleOption.mobileFormat &&
    isMobilePhone(validationNumber, locale as PhoneLocale)
  ) {
    formatToUse = currentLocaleOption.mobileFormat;
  }

  const numberToFormat =
    currentLocaleOption.numberPrefix &&
    cleanedPhoneNumber.startsWith(currentLocaleOption.numberPrefix)
      ? cleanedPhoneNumber.substring(currentLocaleOption.numberPrefix.length)
      : cleanedPhoneNumber;

  let formattedPhone = applyFormat(numberToFormat, formatToUse);

  if (!formattedPhone) {
    return "";
  }

  if (international && currentLocaleOption?.countryCode) {
    if (
      currentLocaleOption.numberPrefix &&
      formattedPhone.startsWith(currentLocaleOption.numberPrefix)
    ) {
      formattedPhone = formattedPhone.substring(
        currentLocaleOption.numberPrefix.length
      );
    }
    formattedPhone = `${
      currentLocaleOption.countryCode
    } ${formattedPhone.trim()}`;
  }
  return formattedPhone;
};

export const getRegionLocaleOptions = (
  locale: PhoneLocale | PhoneLocaleAlias
) => {
  const regionLocaleOptions = [
    naLocaleOptions,
    apacLocaleOptions,
    emeaLocaleOptions,
  ];
  return (
    regionLocaleOptions.find((options) => options[locale]) || naLocaleOptions
  );
};
